import { subject } from "@casl/ability";
import { NetworkClient, useClassFactory, useNetworkService } from "@pasta/box-v2";
import {
  type CreateLetterFileDocumentDto,
  type LetterFileDocumentDto,
  LinkPurpose,
  type UpdateLetterFileDocumentDto,
} from "../dto/letter-file-document.dto";
import { PendingSignature } from "@/app/views/PdfViewer/domain/pending-signature.domain";
import { type PostSignPayload, type PostSignSignature } from "@/app/views/PdfViewer/interfaces/post-sign-payload.dto";

export class LetterFileDocumentApi {
  getBaseEndpoint(letterFileId: number) {
    return `letter-file/${letterFileId}/document/`;
  }

  protected get network(): NetworkClient {
    return useNetworkService();
  }

  map(entity: LetterFileDocumentDto) {
    return subject("LetterFileDocument", entity);
  }

  async create(dto: CreateLetterFileDocumentDto, lfId: number): Promise<LetterFileDocumentDto> {
    const response = await this.network.post(`${this.getBaseEndpoint(lfId)}`, dto);
    return this.map(response.data);
  }

  async update(id: number, lfId: number, dto: UpdateLetterFileDocumentDto): Promise<LetterFileDocumentDto> {
    const response = await this.network.patch(`${this.getBaseEndpoint(lfId)}${id}`, dto);
    return this.map(response.data);
  }

  async replaceDocument(id: number, lfId: number, name: string, fileId: number) {
    await this.network.post(`${this.getBaseEndpoint(lfId)}${id}/replace`, {
      name,
      fileId,
    });
  }

  async trash(id: number, lfId: number) {
    await this.network.put(`${this.getBaseEndpoint(lfId)}${id}/trash`, {});
  }

  async restore(id: number, lfId: number) {
    await this.network.put(`${this.getBaseEndpoint(lfId)}${id}/restore`, {});
  }

  async delete(id: number, lfId: number) {
    await this.network.delete(`${this.getBaseEndpoint(lfId)}${id}`);
  }

  async getLink(id: number, lfId: number, purpose?: LinkPurpose): Promise<string> {
    const purposeQuery = purpose ? `purpose=${purpose}` : "";

    const result = await this.network.get(`${this.getBaseEndpoint(lfId)}${id}/link?${purposeQuery}`);
    return result.data;
  }

  async getPdfUrl(id: number, lfId: number): Promise<string> {
    const result = await this.network.get(`${this.getBaseEndpoint(lfId)}${id}/pdf`);
    return result.data;
  }

  async sign(id: number, lfId: number, signatures: PendingSignature[], pades: boolean): Promise<void> {
    const payload: PostSignPayload = {
      pades,
      signatures: signatures.map((item): PostSignSignature => ({
        x: item.coordinates.upperLeftPoint.x,
        y: item.coordinates.upperLeftPoint.y,
        width: item.coordinates.width,
        height: item.coordinates.height,
        page: item.coordinates.pageIndex,
        signObjectId: item.signObjectId,
        enhanceOptions: item.enhanceOptions,
      })),
    };
    await this.network.post(`${this.getBaseEndpoint(lfId)}${id}/sign`, payload);
  }

  async getVersions(id: number, lfId: number) {
    const response = await this.network.get(`${this.getBaseEndpoint(lfId)}${id}/versions`);
    return (response.data as any[]).map(item => subject("DocumentVersion", item));
  }

  async getVersionLink(vId: number, docId: number, lfId: number) {
    const result = await this.network.get(`${this.getBaseEndpoint(lfId)}${docId}/versions/${vId}/link`);
    return result.data;
  }
}

export const [letterFileDocumentApiToken, useLetterFileDocumentApi] = useClassFactory(LetterFileDocumentApi);
